// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { isEmulatorMode } from "./evalMode.js";
import {
  getRemoteConfig,
  fetchAndActivate,
} from "firebase/remote-config";
import {
  getFirestore,
  connectFirestoreEmulator,
  collection,
  getDocs,
  addDoc,
  Timestamp,
  setDoc,
  doc,
  getDoc,
  query,
  where,
  orderBy,
  updateDoc,
} from "firebase/firestore";
import {
  createUserWithEmailAndPassword,
  getAuth,
  connectAuthEmulator,
  signInWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  sendEmailVerification,
} from "firebase/auth";
import toast, { Toaster } from "react-hot-toast";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { logEvent } from "./evalMode.js";
import isEducationalEmail from "../hooks/isEducationalDiscount.js";

// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// createUserWithEmailAndPassword(auth, email, password)
//   .then((userCredential) => {
//     // Signed up
//     const user = userCredential.user;
//     // ...
//   })
//   .catch((error) => {
//     const errorCode = error.code;
//     const errorMessage = error.message;
//     // ..
//   });

// signInWithEmailAndPassword(auth, email, password)
//   .then((userCredential) => {
//     // Signed in
//     const user = userCredential.user;
//     // ...
//   })
//   .catch((error) => {
//     const errorCode = error.code;
//     const errorMessage = error.message;
//   });

const logInWithEmailAndPassword = async (email, password) => {
  try {
    await signInWithEmailAndPassword(auth, email, password);
    const currentUser = auth.currentUser;
    const userEmail = currentUser.email;
    // Get and print Firebase ID token
    if (process.env.REACT_APP_AUTH_DEBUG === 'true') {
      const idToken = await currentUser.getIdToken();
      console.log("Firebase ID Token:", idToken);
    }

    // Update Firestore if needed
    await updateDoc(doc(db, "users", currentUser.uid), {
      email: userEmail,
      updatedAt: new Date().toISOString(),

    });

  } catch (err) {
    console.error(err);
    toast.error("Incorrect email or password");
  }
};
const logout = async () => {
  const delay = (ms) => new Promise((res) => setTimeout(res, ms));

  await delay(1000);
  // toast("You have been logged out 👋");
  signOut(auth);
};
const registerWithEmailAndPassword = async (
  firstName,
  lastName,
  email,
  password,
  attribution = {}
) => {
  try {
    const res = await createUserWithEmailAndPassword(auth, email, password);
    const user = res.user;
    const attr = attribution && typeof attribution === "object" ? attribution : {};
    await setDoc(doc(db, "users", user.uid), {
      uid: user.uid,
      firstName,
      lastName,
      authProvider: "email",
      email,
      onboarded: "N",
      created: new Date(),
      ...(Object.keys(attr).length
        ? {
            attribution: {
              utm_source: attr.utm_source || null,
              utm_medium: attr.utm_medium || null,
              utm_campaign: attr.utm_campaign || null,
              utm_content: attr.utm_content || null,
              utm_term: attr.utm_term || null,
              landing_role_intent: attr.landing_role_intent || null,
              referrer: attr.referrer || null,
              captured_at: new Date(),
            },
          }
        : {}),
    });
    logEvent(analytics, "user_registered", {
      uid: user.uid,
      firstName,
      lastName,
      authProvider: "email",
      has_attribution: Object.keys(attr).length > 0,
    });
    sendEmailVerification(user);
  } catch (err) {
    // toast.error("Something went wrong when tryin to register");
    // alert(err.message);
  }
};

const getUserPlanData = async (userId) => {
  const userDocRef = doc(db, "users", userId);

  try {
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      console.warn("User document not found");
      return { planKey: null, planActive: false };
    }

    const userData = userDoc.data();
    const subscription = userData.subscription;

    if (!subscription) {
      return { planKey: null, planActive: false };
    }
    if (subscription.status === "past_due") {
      return { planKey: "past_due", planActive: false };
    }
    if (subscription.status === "canceled") {
      return { planKey: "canceled", planActive: false };
    }

    const planKey = subscription?.lookup_key ?? "pro";

    const planActive = subscription?.status === "active" || subscription?.status === "trialing" ? true : false || subscription?.status === "past_due" ? false : false
    const planStatus = planActive

    console.log("planStatus", planStatus)
    return {
      planKey,
      planActive: planStatus,
    };
  } catch (error) {
    console.error("Error fetching user subscription data:", error);
    throw error;
  }
};


// Initialize Firebase
const app = initializeApp(firebaseConfig);
// Eval stack: no Firebase Analytics (it would load gtag from googletagmanager.com).
const analytics = isEmulatorMode ? null : getAnalytics(app);
const db = getFirestore(app);
const auth = getAuth(app);
const functions = getFunctions(app);
const googleProvider = new GoogleAuthProvider();

// Eval stack only: point every Firebase service at the local emulators.
// getAuth()/getStorage()/getFunctions() called elsewhere without an app argument
// resolve to these same default-app instances, so they are connected too.
// Must run before any of the instances is used. Unset in all normal builds.
if (process.env.REACT_APP_USE_EMULATORS === 'true') {
  const emulatorHost = process.env.REACT_APP_EMULATOR_HOST || "localhost";
  connectAuthEmulator(auth, `http://${emulatorHost}:9099`);
  connectFirestoreEmulator(db, emulatorHost, 8080);
  connectStorageEmulator(getStorage(app), emulatorHost, 9199);
  connectFunctionsEmulator(functions, emulatorHost, 5001);
}

const remoteConfig = getRemoteConfig(app);
remoteConfig.settings.minimumFetchIntervalMillis =
  process.env.REACT_APP_RC_MIN_FETCH_MS != null
    ? parseInt(process.env.REACT_APP_RC_MIN_FETCH_MS, 10)
    : process.env.NODE_ENV === "development"
      ? 0
      : 3600000;

remoteConfig.defaultConfig = {
  onboarding_fast_path_percent: "0",
  onboarding_experiment_name: "onboarding_flow_v1",
  onboarding_template_band_director: "",
  onboarding_template_designer: "",
  onboarding_template_instructor: "",
  onboarding_template_student: "",
};

async function ensureRemoteConfig() {
  // Eval stack: never fetch; the local defaultConfig above applies.
  if (isEmulatorMode) return;
  try {
    await fetchAndActivate(remoteConfig);
  } catch (e) {
    console.warn("Remote Config fetch failed; using defaults", e);
  }
}

export {
  db,
  auth,
  functions,
  analytics,
  remoteConfig,
  ensureRemoteConfig,
  logInWithEmailAndPassword,
  registerWithEmailAndPassword,
  logout,
  getUserPlanData,
  googleProvider,
};
